import type { PrincipalCalendarCategory } from 'models';
import { CalendarLineIcon, Empty } from 'ui';
import { EventListItem, EventsListGroup, EventsListGroupSkeleton } from 'features/events';
import type { Mode } from '../../../../types';
import type { ContentEvent } from '../calendar-layout-content';
import { useGetWeekStartsOn } from '../hooks';
import {
  useCreateEventExtra,
  useCreateEventListItem,
  useCreateEventListItems,
  useShouldShowTodayEmptyPlaceholder,
} from './hooks';

export type EventItem = {
  agendaTimeDisplay?: 'allDay' | 'endTime' | 'startTime';
  id: string;
  startTime: Date;
} & (
  | ({
      data: {
        categories: [PrincipalCalendarCategory];
      } & Omit<NodeContentEvent['data'], 'categories'>;
    } & Omit<NodeContentEvent, 'data'>)
  | RailsContentEvent
);
type NodeContentEvent = Extract<ContentEvent, { type: 'node' }>;

type P = {
  date: Date;
  events?: ContentEvent[];
  getPathToEventDetail: (id: string) => string;
  isLoading: boolean;
  mode: Mode;
};

type RailsContentEvent = Extract<ContentEvent, { type: 'rails' }>;

const Agenda = ({ date, events, getPathToEventDetail, isLoading, mode }: P) => {
  const createEventListItem = useCreateEventListItem();
  const weekStartsOn = useGetWeekStartsOn();
  const eventListItems = useCreateEventListItems({ date, events, mode, weekStartsOn });
  const createEventExtra = useCreateEventExtra();
  const shouldShowTodayEmptyPlaceholder = useShouldShowTodayEmptyPlaceholder({
    date,
    events: eventListItems,
    mode,
    weekStartsOn,
  });

  if (isLoading || !eventListItems) {
    return <EventsListGroupSkeleton />;
  }

  if (eventListItems.length === 0) {
    return (
      <Empty
        border
        description={{
          subtitle: 'There are currently no events for selected period',
          title: 'No events',
        }}
        fitCenter
        image={<CalendarLineIcon type="secondary" />}
      />
    );
  }

  return (
    <EventsListGroup<EventItem>
      createItem={(event) => (
        <EventListItem
          event={createEventListItem({ event })}
          extra={createEventExtra({ event })}
          getPathToEventDetail={getPathToEventDetail}
        />
      )}
      events={eventListItems}
      getPathToEventDetail={getPathToEventDetail}
      hideGroupLabel={mode === 'day'}
      // Key is used to force re-render when mode changes to scroll to today group
      key={mode}
      query={{
        fetchNextPage: () => null,
        hasNextPage: false,
        isFetchingNextPage: false,
        isRefetching: false,
      }}
      shouldShowTodayEmptyPlaceholder={shouldShowTodayEmptyPlaceholder}
    />
  );
};

export default Agenda;
